import { defineStore } from "pinia";
import { sdk, idToChannelName } from "@/js/sdk";
import { SdkError, ERROR_CODES, type TaskStatus as SdkTaskStatus } from "@signaloid/cloud-compute-engine-client";
import { isTaskActive, isTaskTerminated, Task, TaskStatus } from "@/types/api/tasks";
import { useEventBus, UseEventBusReturn } from "@vueuse/core";
import * as Sentry from "@sentry/vue";

import type { EventBusKey } from "@vueuse/core";
import { RemoteAccessState, TaskBusEvent, TaskEventE } from "@/types/general";
import { useUserStore } from "./user";

export const taskKey: EventBusKey<TaskBusEvent> = Symbol("symbol-key");

import * as util from "@/js/utilities";
import { UserLimitsE } from "@/js/tierconfig";
import { TierLimitEventTypeE } from "@/eventBus/tierLimitEventBus";
import { useRootStore } from "@/stores/root";
import { monitoringCaptureError } from "@/plugins/monitoring";
import { PartialTask } from "@/js/signaloidClient.types";

type RealtimeSubscription = Awaited<ReturnType<ReturnType<typeof sdk.realtime.channel>["subscribe"]>>;

// Realtime frames deliver the event as a JSON string; id/status sit under its `data`.
type TaskStatusPayload = { taskId?: string; TaskID?: string; status?: string; Status?: string };
type TaskStatusEnvelope = TaskStatusPayload & { data?: TaskStatusPayload };
type TaskStatusMessage = TaskStatusPayload & {
	data?: TaskStatusPayload;
	event?: string | TaskStatusEnvelope;
};

function extractTaskStatusUpdate(msg: TaskStatusMessage): { taskId?: string; status?: string } {
	let event: TaskStatusEnvelope | undefined;
	if (typeof msg?.event === "string") {
		try {
			event = JSON.parse(msg.event);
		} catch {
			event = undefined;
		}
	} else {
		event = msg?.event;
	}
	const payload = event?.data ?? msg?.data ?? event ?? msg;
	return {
		taskId: payload?.taskId ?? payload?.TaskID,
		status: payload?.status ?? payload?.Status,
	};
}

type StoreState = {
	taskHistory: undefined | Task[];
	activeTasks: undefined | Task[];
	taskHistoryUpdateStatus: RemoteAccessState;
	activeTaskUpdateStatus: RemoteAccessState;
	taskMetadataHistory: number[];
	taskEventBus: UseEventBusReturn<TaskBusEvent, any>;
	taskListContinuationKey: undefined | string;
	activeTasksListContinuationKey: undefined | string;
	realtimeSubscription: undefined | RealtimeSubscription;
	realtimeConnecting: boolean;
	realtimeTeardownRequested: boolean;
};

/*
 * given n = tasks returned per request, the SCDP will try to look for
 * active tasks in the first (n * activeTaskMaxFetchDepth) tasks in the databse
 * at the time of writing n = 50
 */
const activeTaskMaxFetchDepth = 3;

export const useTasksStore = defineStore("tasks", {
	state: (): StoreState => ({
		taskHistory: undefined,
		activeTasks: undefined,
		taskHistoryUpdateStatus: { loading: false, error: false, message: "" },
		activeTaskUpdateStatus: { loading: false, error: false, message: "" },
		taskListContinuationKey: undefined,
		activeTasksListContinuationKey: undefined,
		taskMetadataHistory: [],
		taskEventBus: useEventBus(taskKey),
		realtimeSubscription: undefined,
		realtimeConnecting: false,
		realtimeTeardownRequested: false,
	}),
	getters: {
		getTaskByProjectID: (state) => (projectID: string) => {
			// ! Deprecated
			// console.warn("using deprecated function (getTaskByProjectID) :>> ");
			if (!state.taskHistory) {
				return undefined;
			}
			return state.taskHistory.find((x) => x.TaskID === projectID) ?? null;
		},
		getTaskByID: (state) => (taskID: string) => {
			if (!state.taskHistory || !taskID) {
				return undefined;
			}
			return state.taskHistory.find((x) => x.TaskID === taskID) ?? null;
		},
		inactiveTasks(): Task[] {
			if (!this.taskHistory) {
				return [];
			}

			return this.taskHistory.filter((x) => !isTaskActive(x.Status));
		},
	},
	actions: {
		async getTaskByIdOrFetch(taskID: string): Promise<Task> {
			const localTask = this.getTaskByID(taskID);
			if (localTask) {
				return localTask;
			}
			const response = await sdk.tasks.getOne(taskID);
			const taskFromRemote = response as Task;

			this.taskHistory = this.taskHistory ? [taskFromRemote, ...this.taskHistory] : [taskFromRemote];
			return taskFromRemote;
		},
		updateTaskByID(taskData: Task) {
			if (this.taskHistory) {
				const historyIndex = this.taskHistory.findIndex((x) => x.TaskID === taskData.TaskID);
				if (historyIndex !== -1) {
					this.taskHistory.splice(historyIndex, 1, taskData);
				} else {
					this.taskHistory.push(taskData);
				}
				//FIXME: This is wasteful
				this.taskHistory.sort((a, b) => {
					return b.CreatedAt - a.CreatedAt;
				});
			}
			if (this.activeTasks) {
				const activeIndex = this.activeTasks.findIndex((x) => x.TaskID === taskData.TaskID);
				if (activeIndex !== -1) {
					this.activeTasks.splice(activeIndex, 1, taskData);
				} else {
					this.activeTasks.push(taskData);
				}
			}
		},
		removeTaskByIdFromLocal(taskID: string) {
			if (!this.taskHistory) {
				return;
			}
			const newTaskHistory = this.taskHistory.filter((x) => x.TaskID != taskID);
			this.taskHistory = newTaskHistory;
		},
		updateTaskList(taskData: Task) {
			// ! deprecated
			// console.warn("using deprecated function (updateTaskList) :>> ");
			if (this.taskHistory) {
				const historyIndex = this.taskHistory.findIndex((x) => x.TaskID === taskData.TaskID);
				if (historyIndex !== -1) {
					this.taskHistory.splice(historyIndex, 1, taskData);
				} else {
					this.taskHistory.push(taskData);
				}
			}
		},
		removeFromActiveTaskList(taskID: string) {
			if (this.activeTasks) {
				// Filter, not splice, so any duplicate copies are all removed.
				this.activeTasks = this.activeTasks.filter((x) => x.TaskID !== taskID);
			}
		},
		addToActiveTaskList(taskData: Task) {
			if (!this.activeTasks) {
				return;
			}
			// Dedup by TaskID so the optimistic add can't duplicate a WS-pushed task.
			const activeIndex = this.activeTasks.findIndex((x) => x.TaskID === taskData.TaskID);
			if (activeIndex !== -1) {
				this.activeTasks.splice(activeIndex, 1, taskData);
			} else {
				this.activeTasks.push(taskData);
			}
		},
		async initialiseTaskHistory() {
			// Remove all tasks from local storage
			localStorage.removeItem("task-list");
			this.taskListContinuationKey = undefined;
			this.activeTasksListContinuationKey = undefined;
			await this.fetchNextTaskBatchFromRemote();
			await this.fetchActiveTasksFromRemote();
		},

		// Refetch the active list from scratch to recover from missed
		// WS status pushes (sleeping tab, dropped messages, etc.).
		async reconcileActiveTasks() {
			if (this.activeTaskUpdateStatus.loading) return;
			// IDs that drop out of the active list have terminated; resolve each so
			// the status bus and history converge even when the WS missed the push.
			const previouslyActiveIds = (this.activeTasks ?? []).map((task) => task.TaskID);
			this.activeTasksListContinuationKey = undefined;
			await this.fetchActiveTasksFromRemote();
			const stillActiveIds = new Set((this.activeTasks ?? []).map((task) => task.TaskID));
			const terminatedIds = previouslyActiveIds.filter((taskID) => !stillActiveIds.has(taskID));
			await Promise.all(
				terminatedIds.map(async (taskID) => {
					// Skip if the WS already converged it (avoid double-firing completion).
					const known = this.getTaskByID(taskID);
					if (known && isTaskTerminated(known.Status)) return;
					try {
						const finalTask = await sdk.tasks.getOne(taskID);
						this.handleTaskUpdates(finalTask as Task);
					} catch (error) {
						Sentry.captureException(error);
					}
				}),
			);
		},
		async fetchActiveTasksFromRemote() {
			try {
				this.activeTaskUpdateStatus.loading = true;
				let requestCounter = 0;
				do {
					await this.fetchNextActiveTaskBatchFromRemote();
					requestCounter += 1;
				} while (requestCounter < activeTaskMaxFetchDepth && this.activeTasksListContinuationKey !== undefined);
			} catch (error) {
				if (error instanceof SdkError && error.code === ERROR_CODES.API_NOT_FOUND) {
					this.activeTasks = [];
				}
			} finally {
				this.activeTaskUpdateStatus.loading = false;
			}
		},
		async fetchNextActiveTaskBatchFromRemote() {
			try {
				const response = await this.fetchTasksFromRemote(this.activeTasksListContinuationKey, [
					"Accepted",
					"Initialising",
					"In Progress",
				]);

				// Always assign (even on empty) so a finished last task can't linger.
				const fetchedActiveTasks = (response.Tasks as Task[]) ?? [];
				// Dedup by TaskID: concurrent fetches share the continuation key and can re-concat a task.
				this.activeTasks =
					this.activeTasks && this.activeTasksListContinuationKey
						? Array.from(
								new Map(
									this.activeTasks
										.concat(fetchedActiveTasks)
										.map((task): [string, Task] => [task.TaskID, task]),
								).values(),
							)
						: fetchedActiveTasks;
				this.activeTasksListContinuationKey = response.ContinuationKey;
			} catch (error) {
				let isForbiddenError = false;

				if (error instanceof SdkError) {
					if (error.code === ERROR_CODES.API_NOT_FOUND) {
						this.activeTasks = [];
					} else if (error.code === ERROR_CODES.API_FORBIDDEN) {
						isForbiddenError = true;
					}
				}
				if (!isForbiddenError) {
					this.activeTaskUpdateStatus.error = true;
					monitoringCaptureError(error, "Get active tasks from remote");
				}
			}
		},

		async fetchNextTaskBatchFromRemote() {
			try {
				this.taskHistoryUpdateStatus.loading = true;
				const response = await this.fetchTasksFromRemote(this.taskListContinuationKey);
				if (response.Tasks) {
					this.taskHistory =
						this.taskHistory && this.taskListContinuationKey
							? this.taskHistory.concat(response.Tasks as Task[])
							: (response.Tasks as Task[]);
					this.taskListContinuationKey = response.ContinuationKey;

					this.taskHistory.sort((a, b) => b.CreatedAt - a.CreatedAt);
				}
			} catch (error) {
				let isForbiddenError = false;

				if (error instanceof SdkError) {
					if (error.code === ERROR_CODES.API_NOT_FOUND) {
						this.taskHistory = [];
					} else if (error.code === ERROR_CODES.API_FORBIDDEN) {
						isForbiddenError = true;
					}
				}
				// Do not send forbidden errors to sentry.
				if (!isForbiddenError) {
					this.taskHistoryUpdateStatus.error = true;
					monitoringCaptureError(error, "Get tasks from remote");
				}
			} finally {
				this.taskHistoryUpdateStatus.loading = false;
			}
		},

		async fetchTasksFromRemote(startKey?: string, taskStatus?: TaskStatus[]) {
			const authUserStore = useUserStore();
			await util.waitFor(() => authUserStore.currentUserSessionObject !== undefined);
			return sdk.tasks.list({
				startKey,
				status: taskStatus as SdkTaskStatus[] | undefined,
			});
		},
		async getTasksMetadataFromRemote(
			from?: string,
			to?: string,
			startKey?: string,
		): Promise<{ PartialTasks: PartialTask[]; ContinuationKey?: string; Count: number } | null> {
			const authUserStore = useUserStore();
			await util.waitFor(() => authUserStore.currentUserSessionObject !== undefined);
			try {
				const response = await sdk.tasks.listSummary({ from, to, startKey });
				return {
					PartialTasks: response.Tasks as PartialTask[],
					ContinuationKey: response.ContinuationKey,
					Count: response.Count,
				};
			} catch (error) {
				Sentry.captureException(error);
				if (error instanceof SdkError) {
					console.log("Tasks summary fetch failed:", error.code, error.message);
				}
				return null;
			}
		},
		async getTasksMetadataHistoryWithinDays(days: number = 7): Promise<number[]> {
			const authUserStore = useUserStore();
			await util.waitFor(() => authUserStore.currentUserSessionObject !== undefined);

			const today = new Date();
			const fromDate = new Date(
				Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), today.getUTCDate() - (days - 1), 0, 0, 0, 0),
			);
			const toDate = new Date(
				Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), today.getUTCDate(), 23, 59, 59, 999),
			);

			// Optimistic call: try to fetch all tasks for the period in one request
			const response = await this.getTasksMetadataFromRemote(
				fromDate.toISOString(),
				toDate.toISOString(),
				undefined,
			);

			// If fewer than 500 tasks, bucket them by day locally
			if (response && !response.ContinuationKey && response.Count < 500) {
				const counts = this.bucketTasksByDay(response.PartialTasks, days, today);
				this.taskMetadataHistory = counts;
				return counts;
			}

			// Fallback: more than 500 tasks, make individual calls per day
			const dayPromises = Array.from({ length: days }, (_, i) => {
				const dayStart = new Date(
					Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), today.getUTCDate() - i, 0, 0, 0, 0),
				);
				const dayEnd = new Date(
					Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), today.getUTCDate() - i, 23, 59, 59, 999),
				);
				return this.getTaskCountForDay(dayStart.toISOString(), dayEnd.toISOString());
			});

			const counts = await Promise.all(dayPromises);
			this.taskMetadataHistory = counts;
			return counts;
		},
		bucketTasksByDay(tasks: PartialTask[], days: number, today: Date): number[] {
			// Initialize counts array: index 0 = today, index 1 = yesterday, etc.
			const counts = new Array(days).fill(0);

			tasks.forEach((task) => {
				const taskDate = new Date(task.CreatedAt);

				// Calculate which day bucket this task belongs to
				const todayUTC = Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), today.getUTCDate());
				const taskUTC = Date.UTC(taskDate.getUTCFullYear(), taskDate.getUTCMonth(), taskDate.getUTCDate());
				const daysDiff = Math.floor((todayUTC - taskUTC) / (1000 * 60 * 60 * 24));

				if (daysDiff >= 0 && daysDiff < days) {
					counts[daysDiff]++;
				}
			});

			return counts;
		},
		async getTaskCountForDay(from: string, to: string): Promise<number> {
			const response = await this.getTasksMetadataFromRemote(from, to, undefined);

			if (!response) {
				return 0;
			}

			// If there's a continuation key, it means there are more than 500
			// Cap at 500 to indicate "500+"
			return response.Count;
		},

		/*
		 *	Subscribe to the user's realtime task-status channel. The push
		 *	carries taskId + status. On non-terminal transitions we merge the
		 *	status delta into the cached record and skip the REST refetch; on
		 *	terminal status (or first sighting with no cached record) we
		 *	refetch to pick up fields the WS payload doesn't carry (output
		 *	streams, final stats). Idempotent — a second call while a
		 *	subscription is live is a no-op.
		 */
		async initRealtime(userId: string) {
			// realtimeConnecting also guards the window before subscribe() resolves and sets realtimeSubscription.
			if (this.realtimeSubscription || this.realtimeConnecting) {
				return;
			}
			this.realtimeConnecting = true;
			this.realtimeTeardownRequested = false;
			try {
				await sdk.realtime.connect();
				const channel = sdk.realtime.channel(`task-status/${idToChannelName(userId)}`);
				const subscription = await channel.subscribe();
				// Honor a teardown that raced this connect; otherwise the subscription leaks.
				if (this.realtimeTeardownRequested) {
					subscription.close();
					return;
				}
				this.realtimeSubscription = subscription;

				subscription.on("message", async (msg: TaskStatusMessage) => {
					const { taskId, status } = extractTaskStatusUpdate(msg);
					if (!taskId) return;

					const cached = this.getTaskByID(taskId);
					// Ignore pushes for an already-terminal task so we don't re-fire completion.
					if (cached && isTaskTerminated(cached.Status)) {
						return;
					}
					const isTerminal = !!status && isTaskTerminated(status as TaskStatus);
					if (cached && status && !isTerminal) {
						this.handleTaskUpdates({ ...cached, Status: status as TaskStatus });
						return;
					}

					try {
						const response = await sdk.tasks.getOne(taskId);
						this.handleTaskUpdates(response as Task);
					} catch (err) {
						Sentry.captureException(err);
					}
				});
				subscription.on("error", (err: unknown) => {
					console.error("Realtime task channel error:", err);
					monitoringCaptureError(
						err instanceof Error ? err : new Error(String(err)),
						"Realtime task channel error",
						{ severity: "warning" },
					);
				});

				// No snapshot on subscribe: fetch once on connect so an already-running task is not missed.
				await this.fetchActiveTasksFromRemote();
			} catch (err) {
				console.error("Failed to initialize realtime task channel:", err);
				monitoringCaptureError(
					err instanceof Error ? err : new Error(String(err)),
					"Initialize realtime task channel",
					{ severity: "warning" },
				);
			} finally {
				this.realtimeConnecting = false;
			}
		},
		teardownRealtime() {
			// Mark teardown so an in-flight initRealtime() closes its not-yet-assigned subscription.
			this.realtimeTeardownRequested = true;
			if (this.realtimeSubscription) {
				this.realtimeSubscription.close();
				this.realtimeSubscription = undefined;
			}
		},
		handleTaskUpdates(taskData: Task) {
			// update the task object in task history
			this.updateTaskByID(taskData);

			// Check if task is terminated/completed
			if (isTaskTerminated(taskData.Status)) {
				this.removeFromActiveTaskList(taskData.TaskID);

				this.taskEventBus.emit({ type: TaskEventE.TaskCompleted, taskData: taskData });

				const rootStore = useRootStore();
				rootStore.tierLimitEventBus.emit({
					type: TierLimitEventTypeE.UsageChanged,
					affectedLimits: [UserLimitsE.ConcurrentTaskCount, UserLimitsE.DynamicInstructionCount],
				});
				// Update histogram
				this.getTasksMetadataHistoryWithinDays();
				return;
			}
			// Update status of tasks of ongoing tasks
			this.taskEventBus.emit({ type: TaskEventE.TaskUpdated, taskData: taskData });
		},
	},
});
